import LoadingButton from '@/common/components/base/button/loadingButton';
import ModalInput from '@/common/components/base/input/modalInput/ModalInput';
import FreeZonePlateInput from '@/common/components/base/input/plate/freeZonePlateInput/FreeZonePlateInput';
import { FreePlateEmptyRules } from '@/common/components/base/input/plate/freeZonePlateInput/freeZonePlateInput.rules';
import VehiclePlateInput from '@/common/components/base/input/plate/vehiclePlateInput/VehiclePlateInput';
import { VehiclePlateEmptyRules } from '@/common/components/base/input/plate/vehiclePlateInput/vehiclePlateInput.rules';
import RadioGroup from '@/common/components/base/input/radioGroup/RadioGroup';
import {
  PUBLIC_REQUIRED_RULE,
  requiredText,
} from '@/common/utils/rules/publicRules';
import usePartOneHook from '@/modules/adjusterReportModules/IncidentParticipants/components/vehicleInformation/hooks/usePartOneHook';
import { VehicleInformationEnum } from '@/modules/adjusterReportModules/IncidentParticipants/types/VehicleInformationType';
import { Controller, FormProvider } from 'react-hook-form';
import { css } from 'styled-system/css';
import { Box, Flex } from 'styled-system/jsx';

const PartOne = ({ type }: { type: VehicleInformationEnum }) => {
  const {
    VehiclePlateDesignWatch,
    data,
    methods,
    observerRefCallback,
    setName,
    submitHandler,
    vehicleFuel,
    isPending,
    vehicleInformationLoading,
    VehicleTypeWatch,
    VehiclePlateTypeWatch,
    vehicleInformationData,
  } = usePartOneHook(type);
  return (
    <FormProvider {...methods}>
      <Flex direction="column" gap={1}>
        <Box>
          <RadioGroup
            wrapperClassName={css({ mt: '2rem' })}
            className={css({ gap: '2rem' })}
            title="آیا بیمه‌نامه تمدیدی است ؟"
            name="vehicle_insurance_extended"
            options={[
              { label: 'بله', value: true },
              { label: 'خیر', value: false },
            ]}
            renderRadioButton={(Component) => <Box>{Component}</Box>}
          />
        </Box>
        <ModalInput
          name="vehicle_type"
          placeholder="نوع وسیله‌نقلیه"
          label="نوع وسیله‌نقلیه"
          title="نوع وسیله‌نقلیه"
          disabled={!!vehicleInformationData?.vehicle_type?.id}
          options={data.vehicleTypes ?? []}
          rules={{ required: { message: requiredText, value: true } }}
        />
        <ModalInput
          name="vehicle_model"
          placeholder="انتخاب کنید"
          label="سیستم و تیپ"
          title="سیستم و تیپ"
          options={data?.vehicleSystems ?? []}
          loading={data?.isVehicleSystemsPending}
          infiniteScrollRef={observerRefCallback}
          searchOptions={{
            onChange: (e) => {
              setName(e);
            },
            placeholder: 'جستجو کنید',
          }}
          rules={{ required: { message: requiredText, value: true } }}
        />
        <ModalInput
          name="vehicle_use"
          placeholder="انتخاب کنید"
          label="مورد استفاده"
          title="مورد استفاده"
          loading={vehicleInformationLoading}
          options={data?.vehicleUses ?? []}
          rules={{ required: { message: requiredText, value: true } }}
        />
        <ModalInput
          name="vehicle_fuel"
          placeholder="نوع سوخت"
          label="نوع سوخت"
          title="نوع سوخت"
          options={vehicleFuel ?? []}
          rules={{ required: { message: requiredText, value: true } }}
        />
        <ModalInput
          name="vehicle_plate_type"
          placeholder="انتخاب کنید"
          label="نوع پلاک"
          title="نوع پلاک"
          options={data?.plateTypes ?? []}
          rules={{ required: { message: requiredText, value: true } }}
        />

        <ModalInput
          name="vehicle_plate_design"
          label="طرح پلاک"
          title="طرح پلاک"
          options={data?.plateDesigns ?? []}
          rules={{ required: { message: requiredText, value: true } }}
          onChange={() => {
            methods.setValue(
              'vehicle_plate.plate.vehicle_plate_bottom',
              undefined,
            );
            methods.setValue(
              'vehicle_plate.plate.vehicle_plate_top',
              undefined,
            );
          }}
        />
        <ModalInput
          name="vehicle_plate_sample"
          placeholder="انتخاب کنید"
          label="نمونه پلاک"
          title="نمونه پلاک"
          options={data?.plateSamples ?? []}
          disabled={
            !VehicleTypeWatch?.value ||
            !VehiclePlateTypeWatch?.value ||
            !VehicleTypeWatch?.value
          }
          rules={data?.plateSamples?.length ? PUBLIC_REQUIRED_RULE : undefined}
          optionalLabel={!data?.plateSamples?.length}
        />
        {(VehiclePlateDesignWatch?.value === 'country' ||
          !VehiclePlateDesignWatch?.value) &&
        VehicleTypeWatch?.label !== 'موتورسیکلت' ? (
          <Controller
            name="vehicle_plate"
            rules={{
              validate: VehiclePlateEmptyRules,
            }}
            render={({ field, fieldState }) => {
              return (
                <VehiclePlateInput
                  onChange={(v) => {
                    field.onChange(v);
                    if (methods.formState.touchedFields.vehicle_plate) {
                      methods.trigger('vehicle_plate');
                    }
                    v.plateString && methods.setValue('vehicle_plate', v);
                  }}
                  key={VehiclePlateDesignWatch?.value}
                  value={field.value}
                  requiredError={fieldState.error?.message}
                />
              );
            }}
          />
        ) : (
          <Controller
            name="vehicle_plate"
            rules={{
              validate: (value) =>
                FreePlateEmptyRules(
                  value,
                  VehicleTypeWatch?.label === 'موتورسیکلت' &&
                    VehiclePlateDesignWatch?.value === 'free_zone',
                ),
            }}
            render={({ field, fieldState }) => {
              return (
                <FreeZonePlateInput
                  onChange={(v) => {
                    field.onChange(v);
                    if (methods.formState.touchedFields.vehicle_plate) {
                      methods.trigger('vehicle_plate');
                    }
                    v.plateString && methods?.setValue('vehicle_plate', v);
                  }}
                  value={methods.getValues('vehicle_plate')}
                  requiredError={fieldState.error?.message}
                  vehicle_plate_design={
                    VehiclePlateDesignWatch?.value ?? 'country'
                  }
                  vehicle_type_id={VehicleTypeWatch?.value ?? 1}
                  key={VehiclePlateDesignWatch?.value}
                />
              );
            }}
          />
        )}
        <LoadingButton
          loading={isPending}
          onClick={methods?.handleSubmit(submitHandler)}
          type="submit"
          width="100%"
          marginTop="32px"
          disabled={!methods?.formState?.isValid}
        >
          ثبت و ادامه
        </LoadingButton>
      </Flex>
    </FormProvider>
  );
};
export default PartOne;
